import { FormikProps, FormikValues } from 'formik';
import { isEqual } from 'lodash';
import { SOCIAL_PLATFORMS } from 'ui-components';
import { StepperFormStepStateType } from '~/components/StepperForm/StepperForm.types';
import { routes } from '~/core/router';
import { DefaultMediaPackType, GenericIndexSignature } from '~/types';

interface IGetStepStatusProps {
  activeStepIndex: number;
  formikState: FormikProps<FormikValues>;
  mediaPack: GenericIndexSignature<Partial<DefaultMediaPackType>>;
  stepsState: StepperFormStepStateType[];
}

export const getHasChanges = (
  mediaPack: GenericIndexSignature<Partial<DefaultMediaPackType>>,
  currentValues: Partial<DefaultMediaPackType>,
  steps: StepperFormStepStateType[],
) => {
  const values = {
    ...steps.reduce(
      (acc: Partial<DefaultMediaPackType>, { values }) =>
        (acc = { ...acc, ...values }),
      {},
    ),
    ...currentValues,
  };
  return Object.keys(values).reduce(
    (touched: boolean, key: keyof DefaultMediaPackType) => {
      const currentValue = mediaPack[key];
      const updatedValue = values[key];
      if (!touched) {
        touched = !isEqual(currentValue, updatedValue);
      }
      return touched;
    },
    false,
  );
};

const getStepsValidity = (
  activeStepIndex: number,
  stepsStatus: StepperFormStepStateType[],
) =>
  stepsStatus
    .filter((_step, index) => activeStepIndex !== index)
    .map(({ isValid }) => isValid)
    .every((isValid) => isValid);

export const getStepStatus = ({
  activeStepIndex,
  formikState,
  mediaPack,
  stepsState,
}: IGetStepStatusProps) => {
  const hasChangedUpdated = getHasChanges(
    mediaPack,
    formikState.values,
    stepsState,
  );
  const isValid =
    !Object.keys(formikState.errors).length &&
    getStepsValidity(activeStepIndex, stepsState);
  let hasChanged = false;
  if (formikState.isSubmitting) hasChanged = false;
  if (!hasChanged && hasChangedUpdated) hasChanged = true;
  return { isValid, hasChanged };
};

interface ContentLinkOptions {
  hasManager?: boolean;
  id: string;
  isSharedView?: boolean;
  platform?: SOCIAL_PLATFORMS;
  shareLink: string;
}

export const getContentLink = ({
  hasManager,
  id,
  isSharedView,
  platform,
  shareLink,
}: ContentLinkOptions): string => {
  // Content Search
  if (hasManager) {
    return routes.contentSearchDetailModal(id);
  }

  if (isSharedView) {
    return shareLink;
  }

  // TikTok Live Content
  if (platform === SOCIAL_PLATFORMS.tiktok) {
    return routes.creatorliveContentIdTikTok(id);
  }

  // YouTube Live Content
  if (platform === SOCIAL_PLATFORMS.youtube) {
    return routes.creatorliveContentIdYouTube(id);
  }

  // Instagram Live Content
  return routes.creatorLiveContentId(id);
};
